/**
 * Bandeja Express (ADR 025) · NeoCore → Retiros → pestaña "Express".
 *
 * Flujo:
 *   1. Los Express pendientes (sin lote) se listan con su vencimiento.
 *   0. Cada Express pedido le llega por WhatsApp a Elder (y a Hugo): Elder hace toda la operación.
 *   2. "Armar lote y bajar CSV" → crear_lote_retiros_express: fija cuánto se transfiere por cada uno
 *      y baja el CSV ACH de Interbanco (a Hugo le llega el resumen del lote).
 *   3. Elder carga el CSV en Interbanco y acá marca el lote "Ejecutado" con la referencia →
 *      ejecutar_lote_retiros acredita todos los retiros. Si alguno se pasó del vencimiento,
 *      no se cobra la comisión (queda en el ahorro del asociado).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DownloadSimple, Lightning, PaperPlaneTilt, CheckCircle, XCircle } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { bajarCsvAch, lineasAch, type FilaAch } from '@/lib/achInterbanco'

export type ExpressRow = {
  id: string
  estado: string
  tipo: 'normal' | 'express'
  monto: number
  comision: number
  sla_vence_at: string | null
  lote_id: string | null
  asociado_nombre: string
  asociado_neo: string
  asociado_no: string
  banco: string
  tipo_cuenta: string
  numero_cuenta: string
}

type Lote = {
  id: string; numero: number; estado: 'enviado' | 'ejecutado' | 'anulado'
  n_retiros: number; total_transferir: number; vence_primero_at: string | null
  creado_at: string; ejecutado_at: string | null; referencia_banco: string | null
}
type LoteItem = {
  retiro_id: string; estado: string; asociado_nombre: string; asociado_neo: string
  banco: string; tipo_cuenta: string; numero_cuenta: string; monto: number; monto_transferir: number; vence: string
}

const fmtQ = (n: number) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const horaGT = (iso: string | null) => iso
  ? new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
  : '—'
const fechaHoraGT = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
  : '—'
const MARGEN_MS = 10 * 60_000
const aTiempo = (r: ExpressRow, now: number) => !!r.sla_vence_at && now <= new Date(r.sla_vence_at).getTime() + MARGEN_MS
const aTransferir = (r: ExpressRow, now: number) => r.monto - (aTiempo(r, now) ? r.comision : 0)
const hoyCompacto = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }).replace(/-/g, '')

const filaAch = (it: { asociado_nombre: string; asociado_neo: string; banco: string; tipo_cuenta: string; numero_cuenta: string; monto_transferir: number }): FilaAch => ({
  asociado_nombre: it.asociado_nombre, asociado_neo: it.asociado_neo ?? '', banco: it.banco ?? '',
  tipo_cuenta: it.tipo_cuenta ?? '', numero_cuenta: it.numero_cuenta ?? '', monto: Number(it.monto_transferir),
})

export function RetirosExpressBandeja({
  rows, now, onOpen, onReload,
}: {
  rows: ExpressRow[]
  now: number
  onOpen: (id: string) => void
  onReload: () => void
}) {
  const [lotes, setLotes] = useState<Lote[]>([])
  const [busy, setBusy] = useState(false)
  const [ejecutando, setEjecutando] = useState<Lote | null>(null)

  const pendientes = useMemo(
    () => rows.filter((r) => r.tipo === 'express' && r.estado === 'solicitado' && !r.lote_id)
      .sort((a, b) => (a.sla_vence_at ?? '') < (b.sla_vence_at ?? '') ? -1 : 1),
    [rows],
  )
  const totalPend = pendientes.reduce((s, r) => s + aTransferir(r, now), 0)

  const cargarLotes = useCallback(async () => {
    const { data } = await supabase.from('retiro_lote')
      .select('id, numero, estado, n_retiros, total_transferir, vence_primero_at, creado_at, ejecutado_at, referencia_banco')
      .eq('tipo', 'express').order('creado_at', { ascending: false }).limit(20)
    setLotes(((data ?? []) as Lote[]).map((l) => ({ ...l, total_transferir: Number(l.total_transferir) })))
  }, [])
  useEffect(() => { void cargarLotes() }, [cargarLotes])
  useEffect(() => { const t = setInterval(() => { void cargarLotes() }, 60_000); return () => clearInterval(t) }, [cargarLotes])

  const bajarItems = (numero: number, items: LoteItem[]) => {
    const { lineas, sinCodigo } = lineasAch(items.filter((i) => i.estado === 'solicitado').map(filaAch))
    if (sinCodigo.length) {
      alert('Estos retiros tienen un banco sin código ACH y NO van en el archivo (cargalos manual):\n\n' + sinCodigo.join('\n'))
    }
    if (lineas.length === 0) return
    const archivos = bajarCsvAch(lineas, `retiros_express_lote${numero}_${hoyCompacto()}`)
    if (archivos > 1) alert(`Interbanco acepta máx. 9 por archivo: se bajaron ${archivos} archivos. Subilos uno por uno.`)
  }

  const armarLote = async () => {
    if (pendientes.length === 0) return
    if (!confirm(`Armar el lote con ${pendientes.length} retiro(s) Express por ${fmtQ(totalPend)} y bajar el CSV para Interbanco?`)) return
    setBusy(true)
    const { data, error } = await supabase.rpc('crear_lote_retiros_express', { p_retiro_ids: null })
    setBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    const res = data as { numero: number; n: number; total: number; items: LoteItem[] }
    bajarItems(res.numero, (res.items ?? []).map((i) => ({ ...i, estado: 'solicitado' })))
    alert(`Lote Express #${res.numero} armado: ${res.n} retiro(s) · ${fmtQ(Number(res.total))}.\nCargá el CSV en Interbanco y después marcá el lote como Ejecutado con la referencia.`)
    onReload(); void cargarLotes()
  }

  const rebajar = async (l: Lote) => {
    const { data, error } = await supabase.rpc('lote_retiros_items', { p_lote_id: l.id })
    if (error) { alert('Error: ' + error.message); return }
    bajarItems(l.numero, (data ?? []) as LoteItem[])
  }

  const anular = async (l: Lote) => {
    if (!confirm(`¿Anular el lote #${l.numero}? Los retiros vuelven a la bandeja (solo si NO se cargó en el banco).`)) return
    const { error } = await supabase.rpc('anular_lote_retiros', { p_lote_id: l.id })
    if (error) { alert('Error: ' + error.message); return }
    onReload(); void cargarLotes()
  }

  const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }
  const th: React.CSSProperties = { textAlign: 'left', padding: '9px 14px', fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }
  const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13 }

  return (
    <div>
      {/* Pendientes sin lote */}
      <div style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--brand-700)' }}>
            <Lightning size={12} weight="fill" /> Express por armar
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
            {fmtQ(totalPend)}
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)', marginLeft: 8 }}>
              {pendientes.length} {pendientes.length === 1 ? 'retiro' : 'retiros'}
              {pendientes[0]?.sla_vence_at ? ` · el primero vence ${horaGT(pendientes[0].sla_vence_at)}` : ''}
            </span>
          </p>
        </div>
        <button className="btn btn-primary" onClick={armarLote} disabled={busy || pendientes.length === 0}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: pendientes.length === 0 ? 0.5 : 1 }}>
          <PaperPlaneTilt size={14} weight="bold" /> {busy ? 'Armando…' : `Armar lote y bajar CSV (${pendientes.length})`}
        </button>
      </div>

      {pendientes.length > 0 && (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--hairline)' }}>
              <th style={th}>Vence</th><th style={th}>Asociado</th><th style={th}>Cuenta</th>
              <th style={{ ...th, textAlign: 'right' }}>Pidió</th><th style={{ ...th, textAlign: 'right' }}>Transferir</th><th style={th} />
            </tr></thead>
            <tbody>
              {pendientes.map((r) => {
                const min = r.sla_vence_at ? Math.round((new Date(r.sla_vence_at).getTime() - now) / 60_000) : null
                const color = min === null ? 'inherit' : min < 0 ? 'var(--danger, #a32d2d)' : min <= 10 ? 'var(--warning, #d97706)' : 'var(--ink)'
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={{ ...td, fontWeight: 800, color, whiteSpace: 'nowrap' }}>
                      {horaGT(r.sla_vence_at)} <span style={{ fontSize: 11, fontWeight: 700 }}>{min === null ? '' : min < 0 ? '· vencido' : `· ${min} min`}</span>
                    </td>
                    <td style={td}><div style={{ fontWeight: 600 }}>{r.asociado_nombre}</div><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.asociado_no}</div></td>
                    <td style={td}><div style={{ fontWeight: 600 }}>{r.banco}</div><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.tipo_cuenta} · {r.numero_cuenta}</div></td>
                    <td style={{ ...td, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>{fmtQ(r.monto)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
                      {fmtQ(aTransferir(r, now))}
                      {!aTiempo(r, now) && <div style={{ fontSize: 10, color: 'var(--danger, #a32d2d)' }}>sin comisión</div>}
                    </td>
                    <td style={td}><button className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => onOpen(r.id)}>Ver</button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Lotes */}
      <p style={{ margin: '18px 0 8px', fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>
        Lotes Express
      </p>
      <div style={card}>
        {lotes.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 13 }}>Todavía no hay lotes.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--hairline)' }}>
              <th style={th}>Lote</th><th style={th}>Armado</th><th style={th}>Retiros</th>
              <th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th}>Vence el primero</th><th style={th}>Estado</th><th style={th}>Acciones</th>
            </tr></thead>
            <tbody>
              {lotes.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ ...td, fontWeight: 800 }}>#{l.numero}</td>
                  <td style={td}>{fechaHoraGT(l.creado_at)}</td>
                  <td style={td}>{l.n_retiros}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontFeatureSettings: '"tnum"' }}>{fmtQ(l.total_transferir)}</td>
                  <td style={td}>{horaGT(l.vence_primero_at)}</td>
                  <td style={td}>
                    {l.estado === 'enviado' && <span style={{ color: 'var(--warning, #d97706)', fontWeight: 800, fontSize: 11 }}>POR EJECUTAR EN INTERBANCO</span>}
                    {l.estado === 'ejecutado' && <span style={{ color: 'var(--ok-ink, #0f8a57)', fontWeight: 800, fontSize: 11 }}>EJECUTADO {horaGT(l.ejecutado_at)} · ref {l.referencia_banco}</span>}
                    {l.estado === 'anulado' && <span style={{ color: 'var(--muted-foreground)', fontWeight: 800, fontSize: 11 }}>ANULADO</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => void rebajar(l)} title="Bajar el CSV del lote">
                      <DownloadSimple size={12} weight="bold" /> CSV
                    </button>
                    {l.estado === 'enviado' && (
                      <>
                        <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px', marginLeft: 6 }} onClick={() => setEjecutando(l)}>
                          <CheckCircle size={12} weight="bold" /> Ejecutado
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px', marginLeft: 6, color: 'var(--danger, #a32d2d)' }} onClick={() => void anular(l)}>
                          <XCircle size={12} weight="bold" /> Anular
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {ejecutando && (
        <EjecutarLoteModal
          lote={ejecutando}
          onClose={() => setEjecutando(null)}
          onDone={() => { setEjecutando(null); onReload(); void cargarLotes() }}
        />
      )}
    </div>
  )
}

function EjecutarLoteModal({ lote, onClose, onDone }: { lote: Lote; onClose: () => void; onDone: () => void }) {
  const [cuentas, setCuentas] = useState<Array<{ codigo: string; nombre: string; tipo: string }>>([])
  const [cuenta, setCuenta] = useState('')
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    supabase.rpc('listar_cuentas_salida_retiro').then(({ data }) => {
      const list = ((data ?? []) as Array<{ codigo: string; nombre: string; tipo: string }>).filter((c) => c.tipo === 'BANCO')
      setCuentas(list)
      const pre = list.find((c) => c.nombre.toLowerCase().includes('interbanco')) ?? list[0]
      if (pre) setCuenta(pre.codigo)
    })
  }, [])
  const ejecutar = async () => {
    if (!cuenta || !ref.trim()) { alert('Elegí la cuenta de salida y cargá la referencia del lote en Interbanco'); return }
    setBusy(true)
    const { data, error } = await supabase.rpc('ejecutar_lote_retiros', { p_lote_id: lote.id, p_cuenta_salida_codigo: cuenta, p_referencia: ref.trim() })
    setBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    const r = data as { acreditados: number; a_tiempo: number; tarde: number; comisiones: number }
    alert(`Lote #${lote.numero} acreditado: ${r.acreditados} retiro(s).\nA tiempo: ${r.a_tiempo} (comisiones ${fmtQ(Number(r.comisiones))}) · Tarde: ${r.tarde} (comisión devuelta a su ahorro).`)
    onDone()
  }
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.40)', zIndex: 999 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1000, background: '#fff', borderRadius: 14, padding: 22, width: 'min(440px, 92vw)', boxShadow: '0 20px 50px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Lote Express #{lote.numero} · ejecutado en el banco</h3>
        <p style={{ margin: '6px 0 14px', fontSize: 12.5, color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
          {lote.n_retiros} retiro(s) · {fmtQ(lote.total_transferir)}. Al confirmar se acreditan todos y se debita el ahorro de cada asociado.
          La hora de este clic decide si cada Express llegó a tiempo.
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
          Sale de
          <select value={cuenta} onChange={(e) => setCuenta(e.target.value)} style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13 }}>
            {cuentas.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', marginTop: 10 }}>
          Referencia del lote en Interbanco *
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Nº de lote / autorización"
            style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-mono, monospace)' }} />
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</button>
          <button className="btn btn-primary" onClick={ejecutar} disabled={busy} style={{ flex: 2 }}>
            {busy ? 'Acreditando…' : 'Confirmar y acreditar todo'}
          </button>
        </div>
      </div>
    </>
  )
}
